import {
  OriginationPct,
  TermsSection,
  TextFact,
  displayFrequency,
} from "@/components/application/merchant-tasks/config/OfferTerms";
import { useOriginationInput } from "@/components/application/merchant-tasks/config/useOriginationInput";
import {
  formatCurrency,
  formatPercentPoints,
  parseDecimalLike,
} from "@/components/application/merchant-tasks/originationFee";
import { cn } from "@/lib/utils";

import { FEE_KEY, FEE_STEP, type FlowTerms as FlowTermsBlock } from "./payload";

const MONEY_KEYS = new Set(["funding_amount", "merchant_installment"]);

function money(raw: string | number | null | undefined): string {
  const n = typeof raw === "number" ? raw : parseDecimalLike(String(raw ?? ""));
  return n == null ? "" : formatCurrency(n);
}

function shown(key: string, value: string | number | null): string {
  if (value == null || value === "") return "";
  if (MONEY_KEYS.has(key)) return money(value);
  if (key === "frequency") return displayFrequency(String(value));
  return String(value);
}

export interface FlowTermsProps {
  terms: FlowTermsBlock;
  /** The fee as edited (a quarter-point string); undefined while it is the card's. */
  fee: string | undefined;
  onFee: (fee: string | undefined) => void;
  readOnly: boolean;
  /** Edits wait for the card to redraw: the figures below are the card's, not theirs yet. */
  pending: boolean;
}

/**
 * The contract's offer, as the send card's terms read (`OfferTerms`): the calculator's inputs, the
 * origination fee the only one editable here (F1: fee-only until offer-services), then a divider
 * and the figures they fix. The figures are always the server's: an edit redraws the card with
 * its own, and they show as pending until it does.
 */
export function FlowTerms({ terms, fee, onFee, readOnly, pending }: FlowTermsProps) {
  const feeField = terms.fields.find((row) => row.key === FEE_KEY);
  const cardPct = parseDecimalLike(String(feeField?.value ?? "")) ?? 0;
  const pct = fee !== undefined ? (parseDecimalLike(fee) ?? cardPct) : cardPct;
  const input = useOriginationInput(
    pct,
    (next) => onFee(next === cardPct ? undefined : formatPercentPoints(next)),
    FEE_STEP
  );

  const inputs = terms.fields.filter((row) => row.key !== FEE_KEY);
  return (
    <div data-testid="flow-terms" className="space-y-3">
      <TermsSection title="Offer">
        <dl className="space-y-2">
          {inputs.map((row) => (
            <TextFact key={row.key} label={row.label} value={shown(row.key, row.value)} />
          ))}
          <OriginationPct origination={feeField?.editable && !readOnly ? input : pct} />
        </dl>
      </TermsSection>
      {/* The calculator's inputs above, the values they fix below. */}
      <div className="space-y-1.5 border-t pt-3">
        <dl
          role="group"
          aria-label="Derived terms"
          aria-busy={pending || undefined}
          className={cn("space-y-2", pending && "opacity-50")}
        >
          <TextFact label="Payback" value={money(terms.derived.payback)} />
          <TextFact label="Disbursed" value={money(terms.derived.disbursed)} />
          <TextFact label="Origination Fee" value={money(terms.derived.origination_fee_amount)} />
          <TextFact
            label="Specified %"
            value={
              terms.derived.specified_percentage == null
                ? ""
                : `${terms.derived.specified_percentage}%`
            }
          />
        </dl>
        {pending ? (
          <p className="text-[11px] text-muted-foreground">
            Update to see the figures for your changes.
          </p>
        ) : null}
      </div>
    </div>
  );
}
